import { putEvaluation } from '../../API/Employee/Employee'
import React, { useState, useEffect, useRef } from 'react'
import { FaArrowCircleLeft, FaArrowCircleRight, FaRegEdit } from 'react-icons/fa'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'

const CarouselAvaliation = ({ evaluations, selectedEvaluation, setSelectedEvaluation, onEdit }) => {
	const location = useLocation()
	const path = location.pathname
	const isFromEmployee = typeof path === 'string' && path.includes('/employee/')
	const [showEditModal, setShowEditModal] = useState(false)
	const [editName, setEditName] = useState('')
	const [editActual, setEditActual] = useState(false)
	const [editClosed, setEditClosed] = useState(false)
	const [loading, setLoading] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')
	const [currentIndex, setCurrentIndex] = useState(selectedEvaluation ?? 0)
	const [open, setOpen] = useState(false)
	const selectRef = useRef(null)

	useEffect(() => {
		if (selectedEvaluation !== null) {
			setCurrentIndex(selectedEvaluation)
		}
	}, [selectedEvaluation])

	useEffect(() => {
		const handleClickOutside = event => {
			if (selectRef.current && !selectRef.current.contains(event.target)) {
				setOpen(false)
			}
		}
		document.addEventListener('click', handleClickOutside)
		return () => document.removeEventListener('click', handleClickOutside)
	}, [])

	const handlePrev = () => {
		const newIndex = currentIndex === 0 ? evaluations.length - 1 : currentIndex - 1
		setCurrentIndex(newIndex)
		setSelectedEvaluation(newIndex)
	}

	const handleNext = () => {
		const newIndex = currentIndex === evaluations.length - 1 ? 0 : currentIndex + 1
		setCurrentIndex(newIndex)
		setSelectedEvaluation(newIndex)
	}

	const handleSelect = index => {
		setCurrentIndex(index)
		setSelectedEvaluation(index)
		setOpen(false)
	}

	if (evaluations.length === 0 || !evaluations) {
		return <div className='tw-text-gray-400 tw-text-base tw-font-medium'>Nenhuma avaliação disponível</div>
	}

	const current = evaluations[currentIndex]

	function formatDate(isoDate) {
		const date = new Date(isoDate)
		return date.toLocaleDateString('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		})
	}

	const openEditModal = () => {
		setEditName(current?.name || '')
		setEditActual(current?.actual || false)
		setEditClosed(current?.closed || false)
		setShowEditModal(true)
	}

	const handleSubmitEdit = async () => {
		try {
			setLoading(true)
			setErrorMessage('')

			const sendData = {
				name: editName,
				actual: editActual,
				closed: editClosed,
				companyId: JSON.parse(localStorage.getItem('user')).id,
			}

			await putEvaluation(evaluations[selectedEvaluation]?.id, sendData)

			if (onEdit) {
				onEdit()
			}

			setShowEditModal(false)
		} catch (error) {
			const message = error?.message || 'Erro inesperado'
			setErrorMessage(message)
		} finally {
			setLoading(false)
		}
	}

	const hasChanged = editName !== current?.name || editActual !== current?.actual || editClosed !== current?.closed

	return (
		<>
			<div className='tw-relative tw-w-full'>
				<div className='tw-bg-white tw-rounded-xl tw-w-full tw-py-4'>
					<div className='tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-items-start md:tw-items-center tw-gap-4 md:tw-gap-6'>
						<div className='tw-flex tw-items-center tw-gap-2'>
							<div className='tw-text-2xl md:tw-text-3xl tw-font-bold tw-truncate'>{current?.name}</div>
							{!isFromEmployee && (
								<div onClick={openEditModal} className='tw-cursor-pointer tw-ml-2'>
									<FaRegEdit size={20} className='tw-text-purple-500 hover:tw-text-purple-700' />
								</div>
							)}
						</div>

						<div className='tw-flex tw-flex-col sm:tw-flex-row tw-items-start sm:tw-items-center tw-gap-4 sm:tw-gap-6 tw-w-full sm:tw-w-auto'>
							<div className='tw-text-sm xl:tw-text-lg tw-text-gray-500'>
								Creada en {formatDate(current?.createdAt)} {current?.closed ? '- Cerrada' : ''}
							</div>

							<div className='tw-text-sm xl:tw-text-lg tw-text-gray-500'>
								{currentIndex + 1} de {evaluations?.length} Evaluaciones
							</div>

							<div className='tw-flex tw-space-x-2'>
								<FaArrowCircleLeft size={24} onClick={handlePrev} className='tw-cursor-pointer tw-text-gray-500 hover:tw-text-gray-800' />
								<FaArrowCircleRight size={24} onClick={handleNext} className='tw-cursor-pointer tw-text-gray-500 hover:tw-text-gray-800' />
							</div>

							<div className='tw-relative tw-w-full sm:tw-w-64' ref={selectRef}>
								<div
									className='tw-bg-purple-200 tw-rounded-xl tw-px-3 tw-py-2 tw-text-sm xl:tw-text-base tw-flex tw-justify-between tw-items-center tw-cursor-pointer'
									onClick={() => setOpen(!open)}
								>
									<span className='tw-truncate'>{current?.name}</span>
									{open ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
								</div>
								{open && (
									<ul className='tw-list-none tw-absolute tw-z-10 tw-bg-white tw-shadow-md tw-w-full tw-max-h-60 tw-overflow-y-auto tw-mt-1 tw-px-2 tw-py-2 tw-rounded-xl'>
										{[...evaluations]
											.sort((a, b) => a.name.localeCompare(b.name))
											.map((item, index) => (
												<li
													key={index}
													className='tw-text-sm xl:tw-text-base tw-px-2 tw-py-2 tw-cursor-pointer hover:tw-bg-purple-100 tw-rounded-xl'
													onClick={() => handleSelect(index)}
												>
													{item?.name}
												</li>
											))}
									</ul>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
			{showEditModal && (
				<div className='tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50'>
					<div className='tw-bg-white tw-rounded-lg tw-shadow-lg tw-w-[90%] md:tw-w-[500px] tw-p-6'>
						<h2 className='tw-text-xl tw-font-semibold tw-mb-4'>Editar Evaluación</h2>

						<div className='tw-mb-4'>
							<label className='tw-block tw-font-medium tw-mb-1'>Nombre</label>
							<input type='text' value={editName} onChange={e => setEditName(e.target.value)} className='tw-w-full tw-p-2 tw-border tw-rounded' />
						</div>

						<div className='tw-mb-4 tw-flex tw-items-center tw-gap-2'>
							<input
								type='checkbox'
								id='editActual'
								checked={editActual}
								onChange={e => setEditActual(e.target.checked)}
								className='tw-form-checkbox tw-h-4 tw-w-4 tw-text-purple-600 tw-cursor-pointer'
							/>
							<label htmlFor='editActual'>Evaluación actual</label>
						</div>

						<div className='tw-mb-4 tw-flex tw-items-center tw-gap-2'>
							<input
								type='checkbox'
								id='editClosed'
								checked={editClosed}
								onChange={e => setEditClosed(e.target.checked)}
								className='tw-form-checkbox tw-h-4 tw-w-4 tw-text-purple-600 tw-cursor-pointer'
							/>
							<label htmlFor='editClosed'>Evaluación cerrada</label>
						</div>

						<div className='tw-flex tw-justify-end tw-gap-2'>
							<div
								onClick={() => setShowEditModal(false)}
								className='tw-px-4 tw-py-2 tw-bg-gray-200 tw-rounded hover:tw-bg-gray-300 tw-cursor-pointer'
							>
								Cancelar
							</div>
							<div
								onClick={hasChanged && !loading ? handleSubmitEdit : undefined}
								disabled={!hasChanged || loading}
								className={`tw-px-4 tw-py-2 tw-rounded tw-text-white
		${!hasChanged || loading ? 'tw-bg-gray-300' : 'tw-bg-purple-500 hover:tw-bg-purple-600 tw-cursor-pointer'}`}
							>
								{loading ? 'Salvando...' : 'Salvar'}
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	)
}

export default CarouselAvaliation
